import React, { useState, useMemo } from 'react';
import { Sparkles, Target, RefreshCw, ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { OrkaTaskHeader } from './OrkaTaskHeader';
import { OrkaTaskFieldGroups } from './OrkaTaskFieldGroups';
import { OrkaStatusChangeButton } from './OrkaStatusChangeButton';
import { OrkaButton } from '../OrkaButton';
import { TASK_FIELD_GROUPS } from '../../../design-system/taskFieldGroups';
import { getPriority } from '../../../design-system/priority';

/**
 * OrkaTaskCard — V4 canonical task card.
 *
 * Key V4 rules applied here:
 *   - Expand/collapse chevron LIVES IN THE FOOTER STRIP — never an absolute
 *     overlay that collides with the header's move handle, more menu, or pills.
 *   - Closed-card min-height reflects task effort so the card shows its own
 *     duration visually (mappable 1:1 to the Day Planner timeline).
 *   - The big OrkaStatusChangeButton is always visible — in the open body's
 *     action rail when expanded, and inline in the closed summary strip.
 *
 * Modes
 *   open     header + full open body (field groups by default)
 *   closed   compact header + 1-line summary + expand chevron + status button
 *   stacked  open card with a "linked" badge on top
 *
 * Sizing model
 *   closed-height(px) = clamp(72, 72 + effortMinutes * 1.15, 360)
 *   where effortMinutes = task.task_estimated_minutes (fallback: estimated_effort × 15).
 */

function effortMinutes(task) {
  if (typeof task.task_estimated_minutes === 'number') return task.task_estimated_minutes;
  const e = Number(task.task_estimated_effort ?? task.task_effort ?? 0);
  // Effort points → minutes at 15m per point (same as planner hint).
  return Number.isFinite(e) ? e * 15 : 0;
}

function closedMinHeight(task) {
  const minutes = effortMinutes(task);
  return Math.max(72, Math.min(360, 72 + minutes * 1.15));
}

export function OrkaTaskCard({
  task = {},
  mode = 'open',
  defaultOpen = true,
  fieldGroups = false,
  onChange,
  onStatusChange,
  onStartFocus,
  onRecover,
  onMore,
  onReschedule,
  onMove,
  onDragStart,
  onDragEnd,
  onEdit,
  aiNote,
  description,
  className,
  children,
  'data-testid': testId,
}) {
  const [open, setOpen] = useState(mode === 'closed' ? defaultOpen : true);
  const closed = mode === 'closed' && !open;
  const stacked = mode === 'stacked';
  const linked = task.linked ?? task.task_linked_count ?? 0;
  const body = description ?? task.description ?? task.task_description;
  const useFieldGroups = fieldGroups !== false;
  const groupsToRender = Array.isArray(fieldGroups) ? fieldGroups : TASK_FIELD_GROUPS;

  const summary = [
    task.task_estimated_effort != null && `E${task.task_estimated_effort}`,
    effortMinutes(task) ? `${effortMinutes(task)}m` : null,
    task.projectKey,
    task.task_status,
  ].filter(Boolean).join(' · ');

  const minH = useMemo(() => (mode === 'closed' ? closedMinHeight(task) : undefined), [mode, task]);

  const priorityMeta = useMemo(() => getPriority(task?.task_priority), [task]);

  return (
    <div
      className={cn(
        'relative rounded-2xl border orka-border-default orka-surface-raised overflow-hidden flex flex-col',
        'shadow-[0_10px_26px_rgba(0,0,0,0.12)]',
        'orka-transition-colors',
        stacked && 'orka-task-card--stacked',
        className,
      )}
      style={{
        ...(minH ? { minHeight: minH } : {}),
        ...(!closed ? {
          borderColor: priorityMeta.color,
          boxShadow: `0 10px 26px rgba(0,0,0,0.12), 0 0 0 1px ${priorityMeta.border}, 0 0 0 4px ${priorityMeta.soft}`,
        } : {}),
      }}
      data-testid={testId || 'orka-task-card'}
      data-mode={mode}
      data-open={open ? 'true' : 'false'}
      data-effort-minutes={effortMinutes(task) || undefined}
    >
      {stacked && linked > 0 && (
        <div
          className="inline-flex items-center self-start h-5 px-2 m-3 mb-0 rounded-full text-[10px] font-bold uppercase tracking-wider border"
          style={{ color: '#1d4ed8', borderColor: 'rgba(96,165,250,0.55)', backgroundColor: 'rgba(59,130,246,0.14)' }}
          data-testid="orka-task-card-stack-count"
        >
          {linked} linked
        </div>
      )}

      {/* Top-right action pair — canonical chevron + pencil. Same size, both
          28×28 round-rect. Chevron toggles open/closed; pencil opens edit
          modal via onEdit. Rendered for ALL modes so design-system templates
          stay consistent with the V3 effort-tier cards. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Collapse' : 'Expand'}
        className="absolute z-[3] grid place-items-center cursor-pointer orka-transition-colors orka-text-tertiary hover:orka-text-primary"
        style={{
          top: 12, right: 46, width: 28, height: 28, borderRadius: 10,
          border: '1px solid var(--orka-border-default,rgba(120,120,120,.3))',
          background: 'color-mix(in srgb, var(--orka-surface-base, #fff) 86%, transparent)',
          opacity: 0.84,
        }}
        data-testid="orka-task-card-toggle-tr"
      >
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      <button
        type="button"
        onClick={() => onEdit?.(task)}
        aria-label="Edit task"
        className="absolute z-[3] grid place-items-center cursor-pointer orka-transition-colors orka-text-tertiary hover:orka-text-primary"
        style={{
          top: 12, right: 12, width: 28, height: 28, borderRadius: 10,
          border: '1px solid var(--orka-border-default,rgba(120,120,120,.3))',
          background: 'color-mix(in srgb, var(--orka-surface-base, #fff) 86%, transparent)',
          opacity: 0.84,
        }}
        data-testid="orka-task-card-edit-tr"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>

      <OrkaTaskHeader
        task={task}
        variant={closed ? 'compact' : 'board'}
        showZones={!closed}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onMore={onMore}
        onReschedule={onReschedule}
        onMove={onMove}
        onWorkAreaChange={onChange ? (work_area_id, work_area_name) => onChange({ work_area_id, work_area_name }) : undefined}
        onPriorityChange={onChange ? (task_priority) => onChange({ task_priority }) : undefined}
        onEffortChange={onChange ? (task_estimated_effort) => onChange({ task_estimated_effort }) : undefined}
        onTitleChange={onChange ? (task_title) => onChange({ task_title }) : undefined}
        onStatusChange={onChange ? (legacy) => onChange({ task_status: legacy }) : (legacy) => onStatusChange?.(task, legacy)}
        onTimeChange={onChange ? (timeUpdate) => onChange(timeUpdate) : undefined}
      />

      {/* Open body ------------------------------------------------------- */}
      {!closed && (
        <div className="p-4 space-y-3 flex-1">
          {children ? (
            children
          ) : useFieldGroups ? (
            <OrkaTaskFieldGroups task={task} groups={groupsToRender} onChange={onChange} />
          ) : (
            <>
              {body && (
                <p className="text-xs leading-relaxed orka-text-tertiary line-clamp-3" data-testid="orka-task-card-description">{body}</p>
              )}
              {aiNote && (
                <div className="flex items-start gap-2 rounded-xl border px-2.5 py-2 text-xs font-medium"
                     style={{ borderColor: 'rgba(96,165,250,0.55)', background: 'rgba(59,130,246,0.14)', color: '#1d4ed8' }}
                     data-testid="orka-task-card-ai-note">
                  <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>{aiNote}</span>
                </div>
              )}
            </>
          )}

          {/* Action rail — status change is always on the left; secondary actions on the right */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t orka-border-subtle" data-testid="orka-task-card-actions">
            <OrkaStatusChangeButton
              state={task.task_status}
              onChange={(legacy) => onChange ? onChange({ task_status: legacy }) : onStatusChange?.(task, legacy)}
              size="md"
              surface="on-surface"
            />
            <div className="flex items-center gap-2">
              {onStartFocus && <OrkaButton variant="primary" size="sm" icon={Target} onClick={onStartFocus}>Start focus</OrkaButton>}
              {onRecover && <OrkaButton variant="secondary" size="sm" icon={RefreshCw} onClick={onRecover}>Recover</OrkaButton>}
            </div>
          </div>
        </div>
      )}

      {/* Spacer for closed mode so effort-height reads visually */}
      {closed && <div className="flex-1" />}

      {/* Footer strip — just the expand chevron + optional summary now that
          the status pill lives inside the header. */}
      {mode === 'closed' && (
        <div
          className="flex items-center justify-between gap-2 px-3 py-2 border-t orka-border-subtle orka-surface-raised"
          data-testid="orka-task-card-footer"
        >
          {closed && (
            <span className="text-[11px] orka-text-tertiary truncate" data-testid="orka-task-card-summary">
              {summary || '—'}
            </span>
          )}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg border-2 orka-border-default orka-surface-base hover:orka-surface-subtle orka-transition-colors shrink-0"
            aria-label={open ? 'Collapse' : 'Expand'}
            data-testid="orka-task-card-expand"
          >
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      )}
    </div>
  );
}

export default OrkaTaskCard;
